/**
 * Search & insights. One box finds the document that contains something — full-text,
 * ranked (BM25 where the database supports it), scoped to this tenant — so an answer is a
 * query away instead of a scroll. Above it, the corpus at a glance: what has been read,
 * what kind, from where, how much lately. Nothing here names an industry; it counts
 * documents and shows text, the same as every other screen.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import type { DocumentList, DocumentStatus, SearchResponse } from "../lib/types";
import { Shell } from "../components/Shell";
import {
  Button,
  Empty,
  Eyebrow,
  Failed,
  Loading,
  Metric,
  Panel,
  Segmented,
} from "../components/ui";
import { BarList, Donut, Spark, type Slice } from "../components/charts";

const SAMPLE = 200;
const LIMITS = ["10", "25", "50"] as const;

const HISTORY_KEY = "alt.search.history";
const HISTORY_MAX = 12;

function loadHistory(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]");
    return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

const STATUS_COLOR: Record<DocumentStatus, string> = {
  succeeded: "var(--ok)",
  processing: "var(--gold-surface)",
  queued: "var(--text-faint)",
  stale: "var(--warn)",
  failed: "var(--crit)",
  unknown: "var(--hair-strong)",
};

/** Renders a `ts_headline` snippet: matched terms come wrapped in `<b>`; everything else is
 * escaped by React as text, so the document's own content can never inject markup. */
function Snippet({ html }: { html: string }) {
  const parts = html.split(/<b>|<\/b>/);
  return (
    <p className="text-[12.5px] leading-relaxed text-dim">
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="bg-gold-wash font-semibold text-gold">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </p>
  );
}

function tally<T extends string>(values: (T | null | undefined)[]): { key: string; value: number }[] {
  const counts = new Map<string, number>();
  for (const v of values) {
    const k = v ?? "—";
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([key, value]) => ({ key, value }))
    .sort((a, b) => b.value - a.value);
}

/** Uploads per day for the last 14 days, oldest bucket first. */
function daily(dates: (string | null)[]): number[] {
  const days = 14;
  const buckets = new Array(days).fill(0);
  const now = Date.now();
  for (const d of dates) {
    if (!d) continue;
    const t = Date.parse(d);
    if (Number.isNaN(t)) continue;
    const ago = Math.floor((now - t) / 86_400_000);
    if (ago >= 0 && ago < days) buckets[days - 1 - ago] += 1;
  }
  return buckets;
}

export function Search() {
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState<(typeof LIMITS)[number]>("10");
  const [history, setHistory] = useState<string[]>(loadHistory);

  const remember = (q: string) =>
    setHistory((prev) => {
      const next = [q, ...prev.filter((x) => x !== q)].slice(0, HISTORY_MAX);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      return next;
    });

  const run = (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setDraft(trimmed);
    setQuery(trimmed);
    remember(trimmed);
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem(HISTORY_KEY);
  };

  const corpus = useQuery({
    queryKey: ["documents", "insights"],
    queryFn: () => get<DocumentList>(`/v1/documents?limit=${SAMPLE}`),
  });

  const results = useQuery({
    queryKey: ["search", query, limit],
    queryFn: () => get<SearchResponse>(`/v1/search?q=${encodeURIComponent(query)}&limit=${limit}`),
    enabled: query.length > 0,
  });

  const docs = corpus.data?.documents ?? [];
  const stats = useMemo(() => {
    const statusSlices: Slice[] = tally(docs.map((d) => d.status))
      .map((s) => ({
        label: s.key,
        value: s.value,
        color: STATUS_COLOR[s.key as DocumentStatus] ?? "var(--hair-strong)",
      }));
    return {
      total: docs.length,
      parsed: docs.filter((d) => d.status === "succeeded").length,
      behind: docs.filter((d) => d.current === false).length,
      types: tally(docs.map((d) => d.doc_type)),
      sources: tally(docs.map((d) => d.carrier)).filter((s) => s.key !== "—"),
      statusSlices,
      spark: daily(docs.map((d) => d.created_at)),
    };
  }, [docs]);

  const topRank = results.data?.results[0]?.rank ?? 1;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    run(draft);
  };

  return (
    <Shell fill crumb={<span className="text-text">search</span>}>
      <div className="shrink-0">
        <Eyebrow n="06">find</Eyebrow>
        <h2 className="mt-2 text-[1.5rem] leading-none tracking-tighter uppercase">Search &amp; insights</h2>
        <p className="mt-2 max-w-[64ch] text-[12.5px] leading-relaxed text-dim">
          One query across every parsed document — ranked, highlighted, scoped to your key.
          The fastest way to find <em className="text-text not-italic">which</em> document
          holds something before you open it.
        </p>
      </div>

      {/* Insights */}
      {corpus.isPending ? (
        <Loading what="corpus" />
      ) : corpus.isError ? (
        <Failed error={corpus.error} what="corpus" />
      ) : (
        <div className="hairgrid shrink-0 grid-cols-1 lg:grid-cols-3">
          <Panel title="read status" bodyClass="p-3">
            <Donut slices={stats.statusSlices} total={stats.total} unit="docs" size={96} />
            <div className="mt-2.5 flex gap-3 border-t border-hair pt-2.5">
              <Metric label="parsed" value={stats.parsed} valueClass={stats.parsed ? "text-ok" : ""} />
              <Metric
                label="behind"
                value={stats.behind}
                valueClass={stats.behind ? "text-warn" : ""}
                note={stats.behind ? "older parser" : "all current"}
              />
            </div>
          </Panel>
          <Panel title="by kind" bodyClass="p-3">
            <BarList
              items={stats.types.slice(0, 5).map((t) => ({ key: t.key, label: t.key, value: t.value }))}
            />
          </Panel>
          <Panel
            title="added · 14d"
            aside={
              <span className="font-mono text-[10px] tracking-[0.16em] text-faint uppercase">
                newest {SAMPLE}
              </span>
            }
            bodyClass="flex flex-col gap-3 p-3"
          >
            <Spark values={stats.spark} />
            {stats.sources.length > 0 ? (
              <BarList
                items={stats.sources.slice(0, 4).map((s) => ({ key: s.key, label: s.key, value: s.value }))}
                color="var(--info)"
              />
            ) : (
              <p className="font-mono text-[10.5px] tracking-[0.16em] text-faint uppercase">
                no carrier tagged
              </p>
            )}
          </Panel>
        </div>
      )}

      {/* Search */}
      <Panel
        fill
        title="search"
        actions={
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] tracking-[0.16em] text-faint uppercase">limit</span>
            <Segmented options={LIMITS} value={limit} onChange={setLimit} />
          </div>
        }
      >
        <form onSubmit={submit} className="flex shrink-0 gap-2 border-b border-hair bg-hair-soft p-3">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g.  Ddrone INC-32  ·  incurred over 50000  ·  policy number"
            className="min-w-0 flex-1 border border-hair bg-ground px-3 py-2 font-mono text-[12.5px] text-text placeholder:text-faint focus:border-gold focus:outline-none"
          />
          <Button variant="primary" type="submit" disabled={!draft.trim()}>
            search
          </Button>
        </form>

        {history.length > 0 && (
          <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-hair px-3 py-2">
            <span className="eyebrow me-1 shrink-0">recent</span>
            {history.map((h) => (
              <button
                key={h}
                onClick={() => run(h)}
                title={`Search “${h}” again`}
                className={`max-w-[24ch] truncate border px-2 py-0.5 font-mono text-[11px] tracking-[0.04em] transition-colors duration-200 ease-alt ${
                  h === query
                    ? "border-gold bg-gold-wash text-gold"
                    : "border-hair text-dim hover:border-gold hover:text-gold"
                }`}
              >
                {h}
              </button>
            ))}
            <button
              onClick={clearHistory}
              className="ms-auto shrink-0 font-mono text-[10px] tracking-[0.16em] text-faint uppercase hover:text-crit"
            >
              clear
            </button>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto">
          {query.length === 0 ? (
            <Empty
              title="ask the corpus"
              hint="Type a term, a phrase, a claim or policy number, a company name. Results are ranked by relevance and show the matching passage — open the one you want."
            />
          ) : results.isPending ? (
            <Loading what="results" />
          ) : results.isError ? (
            <Failed error={results.error} what="results" />
          ) : results.data.results.length === 0 ? (
            <Empty title="no matches" hint={`Nothing in your documents matches “${query}”. Try fewer or different words.`} />
          ) : (
            <ul className="divide-y divide-hair">
              {results.data.results.map((hit) => (
                <li key={hit.document_id} className="flex flex-col gap-2 px-4 py-3.5">
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/documents/${hit.document_id}?q=${encodeURIComponent(query)}`}
                      className="min-w-0 truncate font-medium hover:text-gold"
                    >
                      {hit.filename ?? <span className="text-faint italic">unnamed</span>}
                    </Link>
                    <span className="ms-auto flex items-center gap-2">
                      <RankBar rank={hit.rank} top={topRank} />
                      <b className="tnum font-mono text-[11px] font-bold text-gold">
                        {hit.rank.toFixed(1)}
                      </b>
                    </span>
                  </div>
                  <Snippet html={hit.snippet} />
                  <Link
                    to={`/documents/${hit.document_id}?q=${encodeURIComponent(query)}`}
                    className="self-start font-mono text-[10px] tracking-[0.16em] text-faint uppercase hover:text-gold"
                  >
                    open →
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>
    </Shell>
  );
}

function RankBar({ rank, top }: { rank: number; top: number }): ReactNode {
  const pct = Math.max(6, Math.min(100, (rank / (top || 1)) * 100));
  return (
    <span className="hidden h-[6px] w-24 bg-hair-soft sm:block" title={`relevance ${rank.toFixed(2)}`}>
      <i className="block h-full bg-gold-surface" style={{ width: `${pct}%` }} />
    </span>
  );
}
